import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  MessageSquare,
  Calendar,
  FileText,
  DollarSign,
  AlertCircle,
  ChevronRight,
  Plus,
  Clock,
  FolderOpen,
  ArrowRight,
  Settings,
  Eye,
  EyeOff,
  X,
  TrendingUp,
  Wallet,
  Timer,
  Inbox,
  CalendarDays,
  Sparkles
} from '../../icons/wp-icons';
import apiClient from '../../api/apiClient';
import { dashboardApi } from '../../api/dashboardApi';
import ZeiterfassungWidget from '../Dashboard/ZeiterfassungWidget';
import ProaktiveAktionenWidget from '../Dashboard/ProaktiveAktionenWidget';
import { usePermissions } from '../../context/PermissionContext';
import { useDeviceType } from '../../hooks/useIsMobile';

const MOBILE_WIDGET_VISIBILITY_KEY = 'dashboard-mobile-widget-visibility-v1';

// Widget-Konfiguration für Mobile
const MOBILE_WIDGET_CONFIG = {
  'ki-vorschlaege': { label: 'KI-Vorschläge', icon: Sparkles, defaultVisible: true },
  'quick-stats': { label: 'Übersicht', icon: TrendingUp, defaultVisible: true },
  'umsatz': { label: 'Umsatz Monat', icon: DollarSign, defaultVisible: true },
  'forderungen': { label: 'Forderungen', icon: Wallet, defaultVisible: true },
  'zeiterfassung': { label: 'Zeiterfassung', icon: Timer, defaultVisible: true },
  'projekte': { label: 'Projekte', icon: FolderOpen, defaultVisible: true },
  'termine': { label: 'Termine', icon: Calendar, defaultVisible: true },
  'anfragen': { label: 'Anfragen', icon: Inbox, defaultVisible: true },
};

const PROJEKT_STATUS_CONFIG = {
  PLANUNG: { label: 'Planung', color: 'bg-gray-100 text-gray-700' },
  IN_BEARBEITUNG: { label: 'In Bearbeitung', color: 'bg-blue-100 text-blue-700' },
  ABGESCHLOSSEN: { label: 'Abgeschlossen', color: 'bg-green-100 text-green-700' },
  ABGERECHNET: { label: 'Abgerechnet', color: 'bg-purple-100 text-purple-700' }
};

export default function MobileDashboard() {
  const { canView } = usePermissions();
  const { isTablet } = useDeviceType();
  const showKiVorschlaege = canView('KI_VORSCHLAEGE');
  const [loading, setLoading] = useState(true);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const [stats, setStats] = useState({
    kunden: 0,
    anfragen: 0,
    termine: 0,
    neueAnfragen: 0
  });
  const [recentAnfragen, setRecentAnfragen] = useState([]);
  const [upcomingTermine, setUpcomingTermine] = useState([]);
  const [offeneForderungen, setOffeneForderungen] = useState(null);
  const [statistiken, setStatistiken] = useState(null);
  const [aktiveProjekte, setAktiveProjekte] = useState([]);

  // Widget-Sichtbarkeit State
  const [widgetVisibility, setWidgetVisibility] = useState(() => {
    const saved = localStorage.getItem(MOBILE_WIDGET_VISIBILITY_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
    const defaults = {};
    Object.keys(MOBILE_WIDGET_CONFIG).forEach(key => {
      defaults[key] = MOBILE_WIDGET_CONFIG[key].defaultVisible;
    });
    return defaults;
  });

  // Widget-Sichtbarkeit toggle
  const toggleWidgetVisibility = useCallback((widgetId) => {
    setWidgetVisibility(prev => {
      const newVisibility = { ...prev, [widgetId]: !prev[widgetId] };
      localStorage.setItem(MOBILE_WIDGET_VISIBILITY_KEY, JSON.stringify(newVisibility));
      return newVisibility;
    });
  }, []);

  // Alle Widgets ein/ausblenden
  const toggleAllWidgets = useCallback((visible) => {
    setWidgetVisibility(() => {
      const newVisibility = {};
      Object.keys(MOBILE_WIDGET_CONFIG).forEach(key => {
        newVisibility[key] = visible;
      });
      localStorage.setItem(MOBILE_WIDGET_VISIBILITY_KEY, JSON.stringify(newVisibility));
      return newVisibility;
    });
  }, []);

  // Prüfe ob Widget sichtbar sein soll
  const isWidgetVisible = useCallback((widgetId) => {
    // KI-Vorschläge hängt auch von Berechtigung ab
    if (widgetId === 'ki-vorschlaege' && !showKiVorschlaege) {
      return false;
    }
    return widgetVisibility[widgetId] !== false;
  }, [widgetVisibility, showKiVorschlaege]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const [kundenRes, anfrogenRes, termineRes, projekteRes] = await Promise.all([
        apiClient.get('/kunden'),
        apiClient.get('/anfragen'),
        apiClient.get('/termine'),
        apiClient.get('/projekte')
      ]);

      const kunden = kundenRes.data;
      const anfragen = anfrogenRes.data;
      const termine = termineRes.data;
      const projekte = projekteRes.data;

      // Aktive Projekte filtern (PLANUNG oder IN_BEARBEITUNG) und die neuesten 5 anzeigen
      const aktiveProjekteListe = projekte
        .filter(p => p.status === 'PLANUNG' || p.status === 'IN_BEARBEITUNG')
        .sort((a, b) => new Date(b.erstelltAm) - new Date(a.erstelltAm))
        .slice(0, 5);
      setAktiveProjekte(aktiveProjekteListe);

      const offeneAnfragen = anfragen.filter(a =>
        a.status === 'neu' || a.status === 'in_bearbeitung'
      );

      const heute = new Date();
      heute.setHours(0, 0, 0, 0);
      const zukunftigeTermine = termine
        .filter(t => new Date(t.datum) >= heute)
        .sort((a, b) => new Date(a.datum) - new Date(b.datum));

      setStats({
        kunden: kunden.length,
        anfragen: offeneAnfragen.length,
        termine: zukunftigeTermine.length,
        neueAnfragen: anfragen.filter(a => a.status === 'neu').length
      });

      setRecentAnfragen(offeneAnfragen.slice(0, 3));
      setUpcomingTermine(zukunftigeTermine.slice(0, 3));

      const [forderungenData, statData] = await Promise.all([
        dashboardApi.getOffeneForderungen(),
        dashboardApi.getStatistiken()
      ]);

      setOffeneForderungen(forderungenData);
      setStatistiken(statData);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isToday = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isTomorrow = (dateString) => {
    const date = new Date(dateString);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.toDateString() === tomorrow.toDateString();
  };

  const formatTerminDate = (dateString) => {
    if (isToday(dateString)) return 'Heute';
    if (isTomorrow(dateString)) return 'Morgen';
    return new Date(dateString).toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className={`${isTablet ? 'px-6 py-4 bg-surface border-b border-border' : 'mobile-header'} flex items-center justify-between`}>
        <h1 className={isTablet ? 'text-2xl font-serif font-bold text-gray-900' : ''}>Dashboard</h1>
        <button
          onClick={() => setShowWidgetSettings(!showWidgetSettings)}
          className={`p-2 rounded-lg ${
            showWidgetSettings ? 'bg-primary text-white' : isTablet ? 'bg-gray-100 text-gray-700' : 'bg-white/20 text-white'
          }`}
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      {/* Widget-Einstellungen Panel */}
      {showWidgetSettings && (
        <div className="mx-4 mt-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-900 text-sm">Widgets</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggleAllWidgets(true)}
                className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded-lg"
              >
                Alle an
              </button>
              <button
                onClick={() => toggleAllWidgets(false)}
                className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded-lg"
              >
                Alle aus
              </button>
              <button
                onClick={() => setShowWidgetSettings(false)}
                className="p-1 hover:bg-gray-100 rounded-lg ml-1"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(MOBILE_WIDGET_CONFIG).map(([widgetId, config]) => {
              const Icon = config.icon;
              const isVisible = isWidgetVisible(widgetId);
              const isDisabled = widgetId === 'ki-vorschlaege' && !showKiVorschlaege;

              return (
                <button
                  key={widgetId}
                  onClick={() => !isDisabled && toggleWidgetVisibility(widgetId)}
                  disabled={isDisabled}
                  className={`flex items-center gap-2 px-2 py-2 rounded-lg border transition-all text-left ${
                    isDisabled
                      ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                      : isVisible
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-gray-50 border-gray-200 text-gray-500'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="text-xs font-medium truncate flex-1">{config.label}</span>
                  {isVisible && !isDisabled ? (
                    <Eye className="w-3 h-3 flex-shrink-0" />
                  ) : (
                    <EyeOff className="w-3 h-3 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Content */}
      <div className={`${isTablet ? 'p-6' : 'p-4'} space-y-4`}>
        {/* KI-Vorschläge Widget */}
        {isWidgetVisible('ki-vorschlaege') && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-gray-900">KI-Vorschläge</h3>
              </div>
            </div>
            <div className="p-4 max-h-[400px] overflow-y-auto">
              <ProaktiveAktionenWidget />
            </div>
          </div>
        )}

        {/* Quick Stats */}
        {isWidgetVisible('quick-stats') && (
        <div className={`grid ${isTablet ? 'grid-cols-4' : 'grid-cols-2'} gap-3`}>
          <Link to="/kunden" className="bg-white p-4 rounded-xl shadow-sm active:bg-gray-50">
            <div className="flex items-center justify-between">
              <Users className="w-8 h-8 text-blue-500" />
              <span className="text-2xl font-bold">{stats.kunden}</span>
            </div>
            <p className="text-sm text-gray-600 mt-2">Kunden</p>
          </Link>

          <Link to="/kalender" className="bg-white p-4 rounded-xl shadow-sm active:bg-gray-50">
            <div className="flex items-center justify-between">
              <Calendar className="w-8 h-8 text-purple-500" />
              <span className="text-2xl font-bold">{stats.termine}</span>
            </div>
            <p className="text-sm text-gray-600 mt-2">Termine</p>
          </Link>

          <div className="bg-white p-4 rounded-xl shadow-sm">
            <div className="flex items-center justify-between">
              <MessageSquare className="w-8 h-8 text-yellow-500" />
              <span className="text-2xl font-bold">{stats.anfragen}</span>
            </div>
            <p className="text-sm text-gray-600 mt-2">Offene Anfragen</p>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm">
            <div className="flex items-center justify-between">
              <Clock className="w-8 h-8 text-red-500" />
              <span className="text-2xl font-bold">{stats.neueAnfragen}</span>
            </div>
            <p className="text-sm text-gray-600 mt-2">Neue Anfragen</p>
          </div>
        </div>
        )}

        {/* Umsatz diesen Monat */}
        {isWidgetVisible('umsatz') && statistiken && (
          <div className="bg-gradient-to-br from-primary to-primary-dark p-4 rounded-xl text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Umsatz diesen Monat</p>
                <p className="text-3xl font-bold mt-1">
                  {statistiken.umsatzMonat.toLocaleString('de-DE', { minimumFractionDigits: 0 })}€
                </p>
              </div>
              <DollarSign className="w-12 h-12 opacity-50" />
            </div>
            <div className="flex gap-4 mt-4 text-sm">
              <div>
                <p className="opacity-75">{statistiken.rechnungenMonat} Rechnungen</p>
              </div>
              <div>
                <p className="opacity-75">{statistiken.kvasMonat} KVAs</p>
              </div>
            </div>
          </div>
        )}

        {/* Offene Forderungen */}
        {isWidgetVisible('forderungen') && offeneForderungen && offeneForderungen.gesamt.summe > 0 && (
          <div className="bg-white p-4 rounded-xl shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Offene Forderungen</h3>
              <Link to="/rechnungen" className="text-primary text-sm">
                Alle ansehen
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-yellow-50 p-3 rounded-lg">
                <p className="text-lg font-bold text-yellow-700">
                  {offeneForderungen.gesamt.summe.toLocaleString('de-DE', { maximumFractionDigits: 0 })}€
                </p>
                <p className="text-xs text-gray-600">Gesamt</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-lg font-bold text-blue-700">
                  {offeneForderungen.versendet.summe.toLocaleString('de-DE', { maximumFractionDigits: 0 })}€
                </p>
                <p className="text-xs text-gray-600">Versendet</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-lg font-bold text-red-700">
                  {offeneForderungen.ueberfaellig.summe.toLocaleString('de-DE', { maximumFractionDigits: 0 })}€
                </p>
                <p className="text-xs text-gray-600">Überfällig</p>
              </div>
            </div>
          </div>
        )}

        {/* Zeiterfassung */}
        {isWidgetVisible('zeiterfassung') && (
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-3">Zeiterfassung</h3>
          <ZeiterfassungWidget />
        </div>
        )}

        {/* Aktive Projekte */}
        {isWidgetVisible('projekte') && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Aktive Projekte</h3>
            <Link to="/projekte" className="text-primary text-sm">
              Alle ansehen
            </Link>
          </div>
          {aktiveProjekte.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              <FolderOpen className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p>Keine aktiven Projekte</p>
              <Link
                to="/projekte/neu"
                className="mt-2 inline-block text-primary text-sm"
              >
                Neues Projekt erstellen
              </Link>
            </div>
          ) : (
            <div>
              {aktiveProjekte.map((projekt, index) => (
                <Link
                  key={projekt.id}
                  to={`/projekte/${projekt.id}`}
                  className={`flex items-center justify-between p-4 active:bg-gray-50 ${
                    index !== aktiveProjekte.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <FolderOpen className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{projekt.titel}</p>
                      <p className="text-sm text-gray-500 truncate">{projekt.kunde?.name || 'Kein Kunde'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      PROJEKT_STATUS_CONFIG[projekt.status]?.color || 'bg-gray-100 text-gray-700'
                    }`}>
                      {PROJEKT_STATUS_CONFIG[projekt.status]?.label || projekt.status}
                    </span>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Anstehende Termine */}
        {isWidgetVisible('termine') && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Anstehende Termine</h3>
            <Link to="/kalender" className="text-primary text-sm">
              Alle ansehen
            </Link>
          </div>
          {upcomingTermine.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              Keine Termine geplant
            </div>
          ) : (
            <div>
              {upcomingTermine.map((termin, index) => (
                <Link
                  key={termin.id}
                  to={`/kalender?terminId=${termin.id}`}
                  className={`flex items-center justify-between p-4 active:bg-gray-50 ${
                    index !== upcomingTermine.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isToday(termin.datum) ? 'bg-red-100' : 'bg-primary-light'
                    }`}>
                      <Calendar className={`w-5 h-5 ${
                        isToday(termin.datum) ? 'text-red-600' : 'text-primary'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {termin.kunde ? termin.kunde.name : (termin.titel || 'Privat-Termin')}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatTerminDate(termin.datum)} um {termin.uhrzeit} Uhr
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Link>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Offene Anfragen */}
        {isWidgetVisible('anfragen') && recentAnfragen.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Offene Anfragen</h3>
              <Link to="/anfragen" className="text-sm text-primary font-medium flex items-center gap-1">
                Alle <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div>
              {recentAnfragen.map((anfrage, index) => (
                <Link
                  key={anfrage.id}
                  to={`/anfragen/${anfrage.id}`}
                  className={`flex items-center justify-between p-4 active:bg-gray-50 ${
                    index !== recentAnfragen.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      anfrage.status === 'neu' ? 'bg-blue-100' : 'bg-yellow-100'
                    }`}>
                      <MessageSquare className={`w-5 h-5 ${
                        anfrage.status === 'neu' ? 'text-blue-600' : 'text-yellow-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{anfrage.betreff}</p>
                      <p className="text-sm text-gray-500 truncate">{anfrage.kunde?.name || 'Unbekannt'}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className={`grid ${isTablet ? 'grid-cols-4' : 'grid-cols-2'} gap-3`}>
          <Link
            to="/rechnungen/neu"
            className="flex items-center justify-center gap-2 bg-primary text-white p-4 rounded-xl font-medium active:bg-primary-dark"
          >
            <Plus className="w-5 h-5" />
            Neue Rechnung
          </Link>
          <Link
            to="/kostenvoranschlaege/neu"
            className="flex items-center justify-center gap-2 bg-white text-primary border border-primary p-4 rounded-xl font-medium active:bg-primary-light"
          >
            <Plus className="w-5 h-5" />
            Neuer KVA
          </Link>
          {isTablet && (
            <>
              <Link
                to="/kunden"
                className="flex items-center justify-center gap-2 bg-white text-gray-700 border border-gray-200 p-4 rounded-xl font-medium active:bg-gray-50"
              >
                <Users className="w-5 h-5" />
                Alle Kunden
              </Link>
              <Link
                to="/projekte/neu"
                className="flex items-center justify-center gap-2 bg-white text-gray-700 border border-gray-200 p-4 rounded-xl font-medium active:bg-gray-50"
              >
                <FolderOpen className="w-5 h-5" />
                Neues Projekt
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
